import moment from "moment";
import { postCall, getCall } from "services";
import {
  AVAILABILITY_DASHBOARD_COLUMNS,
  RESERVATION_LEDGER_COLUMNS,
} from "constants/availabilityConfig";
import { stripSelectAllToken } from "utils/locationFilterUtils";
import {
  AVAILABILITY_SERVICE_URL_QA,
  AVAILABILITY_SERVICE_URL_PREPROD,
  AVAILABILITY_SERVICE_URL_PROD,
  MASTER_DATA_SERVICE_URL_QA,
  MASTER_DATA_SERVICE_URL_PREPROD,
  MASTER_DATA_SERVICE_URL_PROD,
} from "constants";

const hostname = window.location.hostname;
const isLocal = hostname === "localhost";
const isProd = process.env.REACT_APP_ENV === "PROD";
const isPreprod = hostname.includes("preprod");

const getEnvUrl = (qa, preprod, prod) => {
  if (isProd) return prod;
  if (isPreprod) return preprod;
  return qa;
};

const AVAILABILITY_BASE_URL = "/availability-api";

export const MASTER_DATA_BASE_URL = isLocal ? "" : "/master-data-service";

export const AVAILABILITY_URLS = {
  DASHBOARD: `${AVAILABILITY_BASE_URL}/v1/availability/dashboard`,
  RESERVATION_LEDGER: `${AVAILABILITY_BASE_URL}/v1/reservation-ledger/dashboard`,
  ES_MAPPING: `${AVAILABILITY_BASE_URL}/v1/es-mapping/get-mapping`,
};

// ─── Quantity formatting ───────────────────────────────────────────────────────

const formatQuantityWithPrecision = (q, uom, excludeUnitLabel = false) => {
  const qVal = q ?? 0;
  const u = uom?.ui_label_quantity;
  const uomName = (uom?.name || "").toString().toLowerCase().trim();
  const uomUiLabel = (uom?.ui_label_quantity || "")
    .toString()
    .toLowerCase()
    .trim();
  const uomUnit = (uom?.unit || "").toString().toLowerCase().trim();

  const isBagOrBags =
    ["bag", "bags"].includes(uomName) ||
    ["bag", "bags"].includes(uomUiLabel) ||
    ["bag", "bags"].includes(uomUnit);

  let displayQty = qVal;
  if (isBagOrBags) {
    const precision = uom?.quantity_precision;
    if (precision !== undefined && precision !== null) {
      const p = Number(precision);
      const qNum = Number(qVal);
      if (!isNaN(p) && !isNaN(qNum)) {
        displayQty = qNum.toFixed(p);
      }
    }
  }
  return u && !excludeUnitLabel
    ? `${displayQty} ${u}`.trim()
    : String(displayQty);
};

// ─── Cell value resolver for Availability Dashboard ───────────────────────────

export const getAvailabilityCellValue = (field, row, isExport = false) => {
  switch (field) {
    case "es_derived":
      return formatQuantityWithPrecision(
        row?.availability_breakup?.derived ?? row?.es_derived ?? 0,
        row?.uom,
        isExport,
      );
    case "available_qty":
      return formatQuantityWithPrecision(
        row?.available_qty ?? 0,
        row?.uom,
        isExport,
      );
    case "physical_inventory":
      return formatQuantityWithPrecision(
        row?.availability_breakup?.physical ??
          row?.physical_inventory ??
          row?.physical ??
          0,
        row?.uom,
        isExport,
      );
    case "reserved":
      return formatQuantityWithPrecision(
        row?.availability_breakup?.reserved ?? row?.reserved ?? 0,
        row?.uom,
        isExport,
      );
    case "planned_inventory":
      return formatQuantityWithPrecision(
        row?.availability_breakup?.planned ?? row?.planned_inventory ?? 0,
        row?.uom,
        isExport,
      );
    case "eta":
      return row?.eta ? moment(row.eta).format("DD/MM/YYYY hh:mm A") : "-";
    default:
      return row[field] ?? "-";
  }
};

// ─── Cell value resolver for Reservation Ledger ───────────────────────────────

export const getReservationCellValue = (field, row) => {
  switch (field) {
    case "qty":
      return formatQuantityWithPrecision(row?.qty, row?.uom);
    case "event_timestamp":
      return row?.event_timestamp
        ? moment(row.event_timestamp).format("DD/MM/YYYY HH:mm")
        : "-";
    default:
      return row[field] ?? "-";
  }
};

// ─── Request body builder for Reservation Ledger ──────────────────────────────

const buildReservationLedgerBody = (filters, pagination) => {
  const cleanedFilters = Object.entries(filters || {}).reduce(
    (acc, [key, value]) => {
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        value === "all" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return acc;
      }
      acc[key] = value;
      return acc;
    },
    {},
  );

  const body = {
    page: pagination?.page ?? 0,
    size: pagination?.size ?? 50,
    ...cleanedFilters,
  };

  if (Array.isArray(body.vendor_ids)) {
    body.vendor_ids = stripSelectAllToken(body.vendor_ids.map(String));
    if (body.vendor_ids.length === 0) delete body.vendor_ids;
  }

  if (Array.isArray(body.seller)) {
    body.seller = body.seller
      .flat()
      .map((s) => String(s).trim())
      .filter(Boolean);
    if (body.seller.length === 0) delete body.seller;
  } else if (typeof body.seller === "string" && body.seller.trim()) {
    body.seller = [body.seller.trim()];
  } else if (body.seller !== undefined) {
    delete body.seller;
  }

  const fromOk =
    typeof body.from_date === "string" && body.from_date.trim().length > 0;
  const toOk =
    typeof body.to_date === "string" && body.to_date.trim().length > 0;
  if (!fromOk || !toOk) {
    delete body.from_date;
    delete body.to_date;
  }

  if (typeof body.reference_id === "string") {
    body.reference_id = body.reference_id.trim();
    if (!body.reference_id) delete body.reference_id;
  }
  if (typeof body.mm_id === "string") {
    body.mm_id = body.mm_id.trim();
    if (!body.mm_id) delete body.mm_id;
  }

  return body;
};

// ─── API calls ────────────────────────────────────────────────────────────────

export const fetchAvailabilityData = async (body) => {
  const result = {
    columns: AVAILABILITY_DASHBOARD_COLUMNS,
    rows: [],
    metadata: {},
    error: null,
  };
  try {
    const response = await postCall(AVAILABILITY_URLS.DASHBOARD, body);
    if (response?.data) {
      result.metadata = response.page_detail ?? {};
      const mappedRows = (response.data ?? []).map((row, index) => ({
        ...row,
        es_derived:
          row?.availability_breakup?.derived ??
          row?.es_derived ??
          row?.derived ??
          0,
        physical_inventory:
          row?.availability_breakup?.physical ??
          row?.physical_inventory ??
          row?.physical ??
          0,
        reserved: row?.availability_breakup?.reserved ?? row?.reserved ?? 0,
        planned_inventory:
          row?.availability_breakup?.planned ??
          row?.planned_inventory ??
          row?.planned ??
          0,
        _id: String(index + 1),
      }));

      result.rows = mappedRows;
    } else {
      result.error =
        response?.error?.message || "Failed to fetch availability data";
    }
  } catch (err) {
    result.error = err?.data?.message || err?.statusText || "Unexpected error";
  }
  return result;
};

export const fetchReservationLedgerData = async (filters, pagination) => {
  const result = {
    columns: RESERVATION_LEDGER_COLUMNS,
    rows: [],
    metadata: {},
    error: null,
  };
  try {
    const body = buildReservationLedgerBody(filters, pagination);
    const response = await postCall(AVAILABILITY_URLS.RESERVATION_LEDGER, body);
    if (response?.data) {
      result.metadata = response.page_detail ?? {};
      result.rows = (response.data ?? []).map((row, index) => ({
        ...row,
        _id: `${row?.event_id ?? "no_event"}-${
          row?.reference_id ?? row?.mm_id ?? "no_ref"
        }-${index}`,
      }));
    } else {
      result.error =
        response?.error?.message || "Failed to fetch reservation data";
    }
  } catch (err) {
    result.error = err?.data?.message || err?.statusText || "Unexpected error";
  }
  return result;
};

export const fetchESMMMapping = async (mmid) => {
  const result = { rows: [], error: null };
  try {
    const response = await getCall(AVAILABILITY_URLS.ES_MAPPING, { mmid });
    if (response?.data) {
      result.rows = (response.data ?? []).map((row, index) => ({
        ...row,
        _id: String(index + 1),
      }));
    } else {
      result.error = response?.error?.message || "Failed to fetch ES mapping";
    }
  } catch (err) {
    result.error = err?.data?.message || err?.statusText || "Unexpected error";
  }
  return result;
};

export const searchVendors = async (query, context = "SUPPLIER_NAME") => {
  const url = isLocal
    ? `/master-data-service/v1/text/search/vendor`
    : `${MASTER_DATA_BASE_URL}/v1/text/search/vendor`;
  try {
    const response = await getCall(url, { q: query, context });
    return response?.data?.data ?? [];
  } catch {
    return [];
  }
};

export { formatQuantityWithPrecision };
